/**
 * Password reveal toggle.
 *
 * The design puts a show/hide eye button inside the password field
 * (sign-in.html, register.html). WordPress's own login form renders a bare
 * `<input type="password">` with no such control, so this injects one.
 *
 * ── WHY IT BUILDS THE BUTTON INSTEAD OF EXPECTING ONE IN THE MARKUP
 *
 * wp-login.php's form is CORE markup — we do not own it and must not replace
 * the route (the 2FA chain lives there). So the control is added to whatever
 * core rendered, and if core changes that markup the worst case is that the
 * field stays a plain password field: every branch below bails rather than
 * throwing.
 *
 * ── PROGRESSIVE ENHANCEMENT IS THE POINT, NOT A NICETY
 *
 * The field works without this file. Nothing here is required to sign in,
 * which is why it is safe to run on the login screen at all: a JS error must
 * never be able to stop someone authenticating. Hence no dependencies, no
 * framework, and no assumptions beyond "there is a password input".
 *
 * ── ACCESSIBILITY CONTRACT
 *
 *   - `type="button"`      so it never submits the form,
 *   - `aria-pressed`       announces the CURRENT state, and flips on toggle,
 *   - `aria-label`         localized from PHP (bnrmpLoginI18n), updated with
 *                          the state so a screen reader hears what the button
 *                          will DO next,
 *   - the SVG is `aria-hidden` — the button's label carries the meaning,
 *   - focus is never stolen; the control is reachable in normal tab order,
 *     placed immediately after the input it acts on.
 *
 * The field is returned to `type="password"` on submit, so a browser or
 * password manager never sees a plaintext field being posted, and a
 * bfcache restore cannot show the password still revealed.
 */

const DEFAULT_LABELS = {
  showPassword: 'Show password',
  hidePassword: 'Hide password',
};

/**
 * Eye-with-slash glyph, matching the design's 24-unit icons.
 *
 * Built with createElementNS rather than assigned via innerHTML. The string
 * form would be inert today (a module constant, no interpolation), but this
 * file runs on the SIGN-IN screen, and an innerHTML sink sitting next to a
 * password field is an invitation for the next edit to concatenate something
 * into it. There is no sink to misuse if the DOM is built directly.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';
const EYE_PATHS = [
  'M9.88 9.88a3 3 0 1 0 4.24 4.24',
  'M10.73 5.08A10.94 10.94 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68',
  'M6.61 6.61C3.35 8.36 1 12 1 12s3 7 11 7a10.94 10.94 0 0 0 5.08-1.27',
  'M2 2l20 20',
];

function buildEyeIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  // Decorative: the BUTTON carries the accessible name.
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  EYE_PATHS.forEach((d) => {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  });

  return svg;
}

function labels() {
  const injected = typeof window !== 'undefined' ? window.bnrmpLoginI18n : null;
  return { ...DEFAULT_LABELS, ...(injected || {}) };
}

function enhance(input, i18n) {
  // Never enhance the same field twice — `initPasswordReveal` is safe to
  // call again (e.g. if a future screen re-renders its form).
  if (input.dataset.bnrmpReveal === 'on') return;
  input.dataset.bnrmpReveal = 'on';

  // The design wraps the input so the button can sit inside the field box.
  // Reuse an existing wrapper if the markup already has one, otherwise
  // create it — without ever detaching the input from the form, which would
  // drop its value and its name from the submission.
  let wrap = input.parentElement;
  if (!wrap || !wrap.classList.contains('bnrmp-field__control-wrap')) {
    wrap = document.createElement('div');
    wrap.className = 'bnrmp-field__control-wrap';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
  }

  /*
   * The design's password fields carry a `••••••••` placeholder
   * (sign-in.html:98, register.html:139 and :155 — all three previews, so it
   * is a design element and not one screenshot's filled state). Core renders
   * the field with no placeholder at all.
   *
   * 🔴 A PLACEHOLDER IS NOT A VALUE. `input.placeholder` sets the greyed hint
   * shown while the field is EMPTY; it is never submitted, and the field's
   * `value` is not touched here. Nothing else about the input changes — not
   * `type`, not `name="pwd"`, not `autocomplete="current-password"`, not
   * `spellcheck` — because password managers and the 2FA chain read those.
   *
   * Guarded on the field having no placeholder already, so a screen that
   * supplies its own hint keeps it.
   */
  if (!input.getAttribute('placeholder')) {
    input.placeholder = '••••••••';
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'bnrmp-field__reveal';
  button.setAttribute('aria-pressed', 'false');
  button.setAttribute('aria-label', i18n.showPassword);
  button.appendChild(buildEyeIcon());

  button.addEventListener('click', () => {
    const revealed = input.type === 'text';
    input.type = revealed ? 'password' : 'text';
    button.setAttribute('aria-pressed', revealed ? 'false' : 'true');
    button.setAttribute(
      'aria-label',
      revealed ? i18n.showPassword : i18n.hidePassword
    );
    // Keep the caret where the user left it; changing `type` resets it in
    // some browsers, which is disorienting mid-typing.
    if (typeof input.selectionStart === 'number') {
      const pos = input.value.length;
      try {
        input.setSelectionRange(pos, pos);
      } catch (e) {
        /* setSelectionRange throws on some input types; harmless here. */
      }
    }
    input.focus();
  });

  wrap.appendChild(button);

  // Re-hide before the form is submitted. A revealed field would otherwise
  // stay `type="text"` in the bfcache snapshot, so navigating Back after a
  // failed sign-in would redisplay the password in clear.
  const form = input.form;
  if (form) {
    form.addEventListener('submit', () => {
      input.type = 'password';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', i18n.showPassword);
    });
  }
}

export default function initPasswordReveal(root = document) {
  const i18n = labels();
  const inputs = root.querySelectorAll('input[type="password"]');
  inputs.forEach((input) => {
    try {
      enhance(input, i18n);
    } catch (e) {
      // One malformed field must not stop the others, and must never stop
      // the page. The field simply stays a plain password input.
    }
  });
}
